import { RocketIcon } from "@radix-ui/react-icons";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { BabyIcon, BotIcon, CpuIcon, ThumbsUpIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";

import { ErrorBanner } from "@/components/Callout";
import { CanvasEditorMachine } from "@/components/canvas-editor/canvas-editor-machine";
import {
  languageAnalysisCategories,
  LanguageAnalysisCategory,
} from "@/components/canvas-editor/shapes/shapes";
import { defaultFeedVersion } from "@/components/canvas-editor/utils";
import { Description } from "@/components/Description";
import { FeedEditor, FeedEditorHandle } from "@/components/FeedEditor";
import {
  FeedPageHeader,
  FeedPageIcon,
  FeedPageTitle,
} from "@/components/FeedPageHeader";
import { Page } from "@/components/Page";
import { WorkbenchPreviewFeedBase } from "@/components/PreviewFeed";
import {
  contentTypes,
  SimpleEditorContentType,
  SimpleEditorQuickstart,
} from "@/components/SimpleEditor";
import { Spinner } from "@/components/Spinner";
import { Avatar } from "@/components/ui/avatar";
import { Button, ButtonLink } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
  SelectTrigger,
  SimpleSelect,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { H1, H2, H3, P } from "@/components/ui/typography";
import {
  externalFeedsForUserQueryOptions,
  getManifestForAutoMigrateFeedQueryOptions,
  getRelevantAutoMigrateFeedsQueryOptions,
  migrateFeed,
} from "@/lib/api";
import { ExternalFeed, FeedVersion } from "@/lib/types";
import { cn } from "@/lib/utils";

function ChoiceCard({
  title,
  description,
  icon,
  onClick,
  isActive,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  isActive: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "min-w-[250px] flex-1",
        "bg-slate-action px-4 py-3 rounded-lg flex items-center gap-3 text-left cursor-pointer-extra",
        isActive && "bg-cyan-action",
      )}
    >
      <div className="shrink-0">{icon}</div>
      <div className="flex flex-col gap-1">
        <span className="text-lg font-semibold">{title}</span>
        <span className="text-slate-dim text-sm">{description}</span>
      </div>
    </button>
  );
}

export const Route = createFileRoute("/app/_sidebar-layout/migrate")({
  component: RouteComponent,
  validateSearch: (search) => {
    return {
      bskyf: search.bskyf as string | undefined,
    };
  },
  loader: async ({ context: { queryClient }, location }) => {
    void queryClient.prefetchQuery(externalFeedsForUserQueryOptions());

    if ("bskyf" in location.search) {
      await Promise.all([
        queryClient.prefetchQuery(
          getManifestForAutoMigrateFeedQueryOptions(
            location.search.bskyf as string,
          ),
        ),
        queryClient.prefetchQuery(getRelevantAutoMigrateFeedsQueryOptions()),
      ]);
    }
  },
  head: () => ({ meta: [{ title: "Migrate Feed" }] }),
});

function RouteComponent() {
  // Publish the feed
  const { bskyf } = Route.useSearch();
  const navigate = useNavigate({ from: "/app/migrate" });
  const { data: bskyfManifest } = bskyf
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useSuspenseQuery(getManifestForAutoMigrateFeedQueryOptions(bskyf))
    : { data: undefined };
  const { data: relevantAutoMigrateFeeds = [] } = useQuery(
    getRelevantAutoMigrateFeedsQueryOptions(),
  );
  const autoMigrateFeed = relevantAutoMigrateFeeds.find(
    (feed) => feed.uri === bskyf,
  );
  const autoMigrateFeedVersion = useMemo(() => {
    if (!autoMigrateFeed) return undefined;
    return {
      ...defaultFeedVersion,
      manifest: bskyfManifest?.manifest,
    } as FeedVersion;
  }, [autoMigrateFeed, bskyfManifest]);
  const onSubmit = useMutation({
    onError: (error: AxiosError) => console.error(error),
    mutationFn: async () => {
      const feedUri = selectedFeed?.uri || bskyf;

      if (!feedUri || !feedVersion) {
        throw new Error("No feed selected");
      }

      if (!Object.keys(feedVersion.manifest?.filter).length) {
        throw new Error(
          "You need to build your feed first. Use the editor to add filters.",
        );
      }

      const feed = await migrateFeed({
        algorithm_manifest: feedVersion.manifest,
        feed_uri: feedUri,
        order: feedVersion?.order || "new",
        custom_sort_settings: feedVersion.custom_sort_settings,
      });

      await navigate({
        to: "/app/feed/$feedId/view",
        params: { feedId: `${feed.id}` },
      });
    },
  });

  const { data, isPending } = useSuspenseQuery(
    externalFeedsForUserQueryOptions(),
  );
  const [selectedFeed, setSelectedFeed] = useState<ExternalFeed | undefined>(
    undefined,
  );
  const [feedVersion, setFeedVersion] = useState(
    autoMigrateFeedVersion || defaultFeedVersion,
  );
  const [hasAcceptedInitialWarning, setHasAcceptedInitialWarning] =
    useState(false);

  const editorRef = useRef<FeedEditorHandle>(null!);
  const [showPreview, setShowPreview] = useState(false);
  const [editorType, setEditorType] = useState<
    "simple" | "full" | "generate" | undefined
  >();
  const [language, setLanguage] = useState<LanguageAnalysisCategory | "any">(
    "English",
  );
  const [contentType, setContentType] =
    useState<SimpleEditorContentType>("Any");

  if (!hasAcceptedInitialWarning) {
    return (
      <Page>
        <div className="max-w-lg mx-auto flex items-center justify-center flex-1">
          <div className="flex flex-col gap-8 border border-slate-7 dark:border-slatedark-7 p-8 rounded-lg">
            <H1 className="text-red-10 dark:text-reddark-10">
              Migration Warning
            </H1>
            <div className="flex flex-col gap-4">
              <P>
                This action will{" "}
                <b className="font-bold">
                  transfer hosting of your feed to Graze
                </b>
                , allowing Graze to manage adding new posts.
              </P>
              <P>
                You'll{" "}
                <b className="font-bold">
                  need to recreate your feed's logic in Graze
                </b>{" "}
                since we can't migrate settings from other platforms.
              </P>
              <P>
                Your feed's{" "}
                <b className="font-bold">
                  existing posts and followers will remain unchanged
                </b>
                . After migration, new posts will be added to your feed based on
                your feed configuration.
              </P>
              <P>
                We suggest testing with a less important feed first to ensure
                Graze meets your needs, as this transfer cannot be undone
                through our platform.
              </P>
            </div>
            <div className="flex gap-4">
              <ButtonLink className="flex-1" variant="outline" to="/app">
                Cancel
              </ButtonLink>
              <Button
                className="flex-1"
                onPress={() => setHasAcceptedInitialWarning(true)}
              >
                I understand, continue
              </Button>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  const activeFeed = bskyf ? autoMigrateFeed : selectedFeed;

  return (
    <Page>
      {onSubmit.error && <ErrorBanner error={onSubmit.error} />}

      <FeedPageHeader>
        <FeedPageTitle
          title="Migrate your feed to Graze"
          icon={
            <FeedPageIcon>
              <RocketIcon />
            </FeedPageIcon>
          }
        />
      </FeedPageHeader>

      {isPending ? (
        <Spinner />
      ) : (
        <form
          className="flex flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit.mutate();
          }}
          onInvalid={() => {
            requestAnimationFrame(() => {
              if (!document.activeElement?.closest(".canvas-editor")) {
                editorRef.current.setFullscreen(false);
              }
            });
          }}
        >
          {bskyf ? (
            <div className="flex flex-col gap-4">
              <P>
                We've detected that you're migrating a feed from
                BlueskyFeeds.com.
              </P>
              <P>
                Graze can migrate most feeds from BlueskyFeeds.com automically,
                but some feeds may require manual configuration. You should
                check the feed generated below to ensure it's correct.
              </P>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-1">
                <P>Choose a feed that you already own to move to Graze.</P>
                <P>
                  This process will <b>move all the posts</b> that are currently
                  in your feed to Graze and{" "}
                  <b>won't affect it's existing following</b>.
                </P>
              </div>
              <div className="flex flex-col gap-4">
                <Label>External Feed</Label>
                <Select
                  name="feed"
                  value={selectedFeed?.cid || ""}
                  onValueChange={(cid) =>
                    setSelectedFeed(data.algorithms.find((f) => f.cid === cid))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select one of your feeds" />
                  </SelectTrigger>
                  <SelectContent>
                    {data.algorithms.map((feed) => {
                      const shortCode = feed.uri.split("/").pop();
                      return (
                        <SelectItem key={feed.cid} value={feed.cid}>
                          <div className="gap-3 flex items-center">
                            <span>{feed.display_name}</span>
                            <div className="bg-slate-6 dark:bg-slatedark-6 group-hover:bg-slate-8 dark:group-hover:bg-slatedark-8 group-focus:bg-slate-8 dark:group-focus:bg-slatedark-8 inline-flex px-2 py-1 rounded-md">
                              {shortCode}
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <P>
                Graze doesn&apos;t have the ability to grab the code for your
                old feed. You&apos;ll need to recreate it from scratch in our
                UI.
              </P>
              <Separator />
            </>
          )}

          {activeFeed && (
            <div className="flex-1 flex flex-col gap-6">
              <div className="flex gap-4 items-center justify-between">
                <div className="flex gap-3 items-center">
                  <Avatar
                    size="lg"
                    src={activeFeed.avatar || ""}
                    alt={activeFeed.display_name}
                  />
                  <div className="flex flex-col gap-1">
                    <div className="text-lg">{activeFeed.display_name}</div>
                    {activeFeed.description && (
                      <Description className="text-sm text-slate-dim">
                        {activeFeed.description}
                      </Description>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 items-center rounded-lg bg-slate-3 dark:bg-slatedark-4 px-3 py-1.5 text-sm text-slate-dim">
                  <ThumbsUpIcon className="h-4 w-4 text-slate-dim" />
                  <span>{activeFeed.like_count}</span>
                </div>
              </div>
              <Separator />

              <div className="flex flex-wrap gap-4 mx-auto">
                <ChoiceCard
                  title="Simple Feed"
                  description="Start with a simple feed and build from there. Can escape to the full editor when ready."
                  icon={<BabyIcon className="h-5 w-5" />}
                  onClick={() => setEditorType("simple")}
                  isActive={editorType === "simple"}
                />
                <ChoiceCard
                  title="Full Editor"
                  description="Use graze's full power to build your feed's logic."
                  icon={<CpuIcon className="h-5 w-5" />}
                  onClick={() => setEditorType("full")}
                  isActive={editorType === "full"}
                />
                <ChoiceCard
                  title="Generate"
                  description="Prompt an AI to generate a starting point for your feed."
                  icon={<BotIcon className="h-5 w-5" />}
                  onClick={() => setEditorType("generate")}
                  isActive={editorType === "generate"}
                />
              </div>
              <Separator />

              {editorType === "simple" ? (
                <>
                  <H3 className="mt-0">Settings</H3>
                  <div className="flex items-center">
                    <div className="flex-1">
                      <div className="flex flex-col gap-1 max-w-[60ch]">
                        <Label htmlFor="public-mode">Language</Label>
                        <div className="text-sm text-slate-dim">
                          Only show posts in the following languages.
                        </div>
                      </div>
                    </div>
                    <SimpleSelect
                      aria-label="Language options"
                      options={[...languageAnalysisCategories, "any"]}
                      value={language}
                      onValueChange={setLanguage as (value: string) => void}
                    />
                  </div>
                  <div className="flex items-center mb-6">
                    <div className="flex-1">
                      <div className="flex flex-col gap-1 max-w-[60ch]">
                        <Label htmlFor="public-mode">Content Type</Label>
                        <div className="text-sm text-slate-dim">
                          Only show posts of the following content types.
                        </div>
                      </div>
                    </div>
                    <SimpleSelect
                      aria-label="Content Type options"
                      options={contentTypes}
                      value={contentType}
                      onValueChange={setContentType as (value: string) => void}
                    />
                  </div>
                  <SimpleEditorQuickstart
                    onChange={setFeedVersion}
                    onFinish={() => setShowPreview(true)}
                    language={language}
                    contentType={contentType}
                  />
                  {/* eslint-disable-next-line no-constant-binary-expression */}
                  {showPreview && false && (
                    <>
                      <div className="flex flex-col gap-4">
                        <H2 className="mb-2">Preview</H2> {}
                        <div className="border border-slate-7 dark:border-slatedark-7 rounded-lg min-h-[200px] max-h-[80vh] h-screen">
                          {/* eslint-disable-next-line @eslint-react/no-context-provider */}
                          <CanvasEditorMachine.Provider
                            options={{
                              input: { visiblePanel: "feed" },
                            }}
                          >
                            <WorkbenchPreviewFeedBase
                              feedId={undefined}
                              feedVersion={feedVersion}
                            />
                          </CanvasEditorMachine.Provider>
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : editorType === "full" ? (
                <FeedEditor
                  key="full"
                  handle={editorRef}
                  feedVersion={feedVersion}
                  onChange={setFeedVersion}
                  centerContent={true}
                />
              ) : editorType === "generate" ? (
                <FeedEditor
                  key="generate"
                  handle={editorRef}
                  feedVersion={feedVersion}
                  onChange={setFeedVersion}
                  centerContent={true}
                  templateMode={true}
                />
              ) : null}
            </div>
          )}

          {activeFeed && (
            <div className="flex items-center justify-between gap-4">
              <div></div>
              <div className="flex gap-2">
                <ButtonLink size="lg" variant="outline" to="/app">
                  Cancel
                </ButtonLink>
                <Button
                  size="lg"
                  isLoading={onSubmit.isPending}
                  disabled={!activeFeed || !feedVersion || !editorType}
                  type="submit"
                >
                  Migrate
                </Button>
              </div>
            </div>
          )}
        </form>
      )}
    </Page>
  );
}
